import { useState, useEffect, useRef, useMemo, useLayoutEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import { Users as UsersIcon, Plus, Search, Pencil, Trash2, X, SlidersHorizontal, Eye, EyeOff, Table2, Rows3 } from 'lucide-react';
import { FormModal } from '../../components/ui/FormModal';
import { Button, Toast } from '../../components/ui/FormField';
import { Field, TextInput, SelectInput, PrimaryButton, GhostButton } from '../../components/ui/Field';
import { PasswordStrength } from '../../components/ui/PasswordStrength';
import { SortHeader, makeSortToggle } from '../../components/ui/SortHeader';
import { IconButton, Cell, PaginationControls, GradientOverlayActions, ViewModeSwitch } from '../../components/ui/grid';
import { useCrudPage } from '../../hooks/useCrudPage';
import { useAutoCreate } from '../../hooks/useAutoCreate';
import { usePageHeader } from '../../stores/page-store';
import { useActivityStore } from '../../stores/activity-store';
import { userApi, companyApi, roleApi } from '../../services/api-services';
import { useAuthStore } from '../../stores/auth-store';
import type { User, Company } from '../../types/api';

// Comfortable: Username | Email | Company | First | Last | Actions
const FULL_TEMPLATE =
  'minmax(180px,1.4fr) minmax(200px,1.6fr) minmax(160px,1.2fr) minmax(120px,1fr) minmax(120px,1fr) 116px';
// Compact: Username | Email | Company | Name (no Actions — hover overlay)
const COMPACT_TEMPLATE =
  'minmax(220px,1.6fr) minmax(250px,1.8fr) minmax(190px,1.3fr) minmax(170px,1.2fr)';

type UserView = 'comfortable' | 'compact';
const VIEW_MODE_KEY = 'users.viewMode';
function loadUserView(): UserView {
  try { const v = localStorage.getItem(VIEW_MODE_KEY); if (v === 'compact' || v === 'comfortable') return v; } catch { /* ignore */ }
  return 'comfortable';
}

// ── Validation (unchanged from the old UI) ───────────────────
const PASSWORD_PATTERN = /^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[$@$!%*?&]).{8,}$/;

const emptyUser: Partial<User> = {
  username: '', email: '', password: '', firstName: '', lastName: '', companyId: null,
};

interface UserErrors {
  firstName?: string;
  lastName?: string;
  email?: string;
  username?: string;
  password?: string;
  confirmPassword?: string;
  roleId?: string;
  companyId?: string;
}

function validateUser(form: Partial<User>, roleId: number, confirmPassword: string, isEdit: boolean): UserErrors {
  const e: UserErrors = {};
  if (!form.firstName?.trim()) e.firstName = 'First Name is required';
  if (!form.lastName?.trim()) e.lastName = 'Last Name is required';
  if (!form.email?.trim()) e.email = 'Email is required';
  else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = 'Email must be a valid email address';
  if (!form.username?.trim()) e.username = 'Username is required';
  if (!isEdit) {
    if (!form.password?.trim()) e.password = 'Password is required';
    else if (!PASSWORD_PATTERN.test(form.password))
      e.password = 'Must be 8+ chars with uppercase, lowercase, number and special character ($@$!%*?&)';
  }
  if (isEdit && form.password && !PASSWORD_PATTERN.test(form.password)) {
    e.password = 'Must be 8+ chars with uppercase, lowercase, number and special character ($@$!%*?&)';
  }
  if (form.password && form.password !== confirmPassword) e.confirmPassword = 'Passwords must match';
  if (!roleId) e.roleId = 'User Role is required';
  if (!form.companyId && form.companyId !== 0) e.companyId = 'Company is required';
  return e;
}

export function UsersPage() {
  const authUser = useAuthStore((s) => s.user);
  const isSuperAdmin = authUser?.role === 'SuperAdmin';
  const isCompanyAdmin = authUser?.role === 'CompanyAdmin';
  const recordActivity = useActivityStore((s) => s.record);

  const fetchFn = isSuperAdmin
    ? userApi.getAll
    : (q: Parameters<typeof userApi.getAllByCompanyId>[1]) => userApi.getAllByCompanyId(Number(authUser?.companyId ?? 0), q);

  const crud = useCrudPage<User>({ fetchFn, initialSortCol: 'Username' });
  const [form, setForm] = useState<Partial<User>>(emptyUser);
  const [roleId, setRoleId] = useState(3);
  const [saving, setSaving] = useState(false);
  const [companies, setCompanies] = useState<Company[]>([]);
  const [roles, setRoles] = useState<{ id: number; name: string }[]>([]);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [errors, setErrors] = useState<UserErrors>({});
  const [submitted, setSubmitted] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  // View density (persisted) + filters (client-side on the loaded page)
  const [viewMode, setViewModeRaw] = useState<UserView>(loadUserView);
  const setViewMode = (v: UserView) => { setViewModeRaw(v); try { localStorage.setItem(VIEW_MODE_KEY, v); } catch { /* ignore */ } };
  const compact = viewMode === 'compact';
  const [companyFilter, setCompanyFilter] = useState<number | ''>('');
  const [roleFilter, setRoleFilter] = useState<number | ''>('');

  // Load roles based on current user's role (matching V2)
  useEffect(() => {
    if (isSuperAdmin) {
      companyApi.getAllActive({ limit: 1000 }).then((r) => setCompanies(r.entities ?? []));
      roleApi.getAll().then((r) => setRoles(r.entities ?? [])).catch(() => {});
    } else if (isCompanyAdmin) {
      roleApi.getAdminRoles().then((r) => setRoles(r.entities ?? [])).catch(() => {});
    } else {
      roleApi.getUserRoles().then((r) => setRoles(r.entities ?? [])).catch(() => {});
    }
  }, [isSuperAdmin, isCompanyAdmin]);

  const openEdit = (item: User) => {
    setForm({ ...item, password: '' });
    const firstRole = item.userRoles?.[0];
    setRoleId(firstRole?.roleId ?? 3);
    setConfirmPassword('');
    setErrors({});
    setSubmitted(false);
    setShowPw(false);
    setShowConfirm(false);
    crud.openEdit(item);
  };

  const openCreate = () => {
    setForm({ ...emptyUser, companyId: isSuperAdmin ? null : Number(authUser?.companyId ?? 0) });
    setRoleId(roles.length > 0 ? roles[roles.length - 1].id : 3);
    setConfirmPassword('');
    setErrors({});
    setSubmitted(false);
    setShowPw(false);
    setShowConfirm(false);
    crud.openCreate();
  };
  useAutoCreate(openCreate);

  // Username = Email (always, matching V2). Readonly on create and edit.
  const handleEmailChange = (email: string) => {
    set('email', email);
    if (!crud.editingItem) {
      set('username', email);
    }
  };

  const passwordMismatch = !!form.password && form.password !== confirmPassword;

  const handleSave = async () => {
    setSubmitted(true);
    const e = validateUser(form, roleId, confirmPassword, !!crud.editingItem);
    setErrors(e);
    if (Object.keys(e).length > 0) return;
    setSaving(true);
    try {
      const selectedRole = roles.find(r => r.id === roleId);
      const selectedCompany = companies.find(c => c.id === form.companyId);
      const payload = {
        id: form.id ?? 0,
        username: form.username ?? '',
        email: form.email ?? '',
        password: form.password ?? '',
        companyId: form.companyId ?? null,
        firstName: form.firstName ?? '',
        lastName: form.lastName ?? '',
        // Preserve existing hidden fields from V2 (address, city, state, postal, country, initials)
        initials: String((form as unknown as Record<string, unknown>).initials ?? ''),
        address: String((form as unknown as Record<string, unknown>).address ?? ''),
        city: String((form as unknown as Record<string, unknown>).city ?? ''),
        stateProvince: String((form as unknown as Record<string, unknown>).stateProvince ?? ''),
        postalZipCode: String((form as unknown as Record<string, unknown>).postalZipCode ?? ''),
        country: String((form as unknown as Record<string, unknown>).country ?? ''),
        roleId,
        companyName: selectedCompany?.companyName ?? '',
        roleName: selectedRole?.name ?? '',
      };
      const res = crud.editingItem
        ? await userApi.update(payload)
        : await userApi.create(payload);
      if (res?.status === 'Failed') {
        crud.showToast(res.error?.errorMessage || 'Failed to save user', 'error');
        return;
      }
      const label = (form.username || form.email || 'User').trim();
      crud.showToast(crud.editingItem ? 'User updated successfully' : 'User created successfully');
      recordActivity({ type: crud.editingItem ? 'update' : 'create', entity: 'Users', label, user: authUser?.username });
      crud.closeModal();
      crud.load();
    } catch {
      crud.showToast('Failed to save user', 'error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: number, name?: string) => {
    if (!confirm('Are you sure you want to delete this user?')) return;
    try {
      await userApi.delete(id);
      crud.showToast('User deleted');
      recordActivity({ type: 'delete', entity: 'Users', label: name?.trim() || `User #${id}`, user: authUser?.username });
      crud.load();
    } catch {
      crud.showToast('Failed to delete user', 'error');
    }
  };

  const set = (key: string, value: string | number | null) => setForm((prev) => ({ ...prev, [key]: value }));

  const handleSort = makeSortToggle(crud.sortCol, crud.sortDir, crud.setSortCol, crud.setSortDir);

  // Publish title + primary action into the TopBar. Deps keep the published
  // button's openCreate current as auth/roles hydrate (so the Company default
  // and role default are correct, not captured from the first render).
  usePageHeader('Users', <Button onClick={openCreate}><Plus size={16} /> Add User</Button>, [isSuperAdmin, roles, authUser?.companyId]);

  // Client-side filters on the current page.
  const filteredData = useMemo(() => crud.data.filter((u) => {
    if (companyFilter !== '' && (u.companyId ?? null) !== companyFilter) return false;
    if (roleFilter !== '' && (u.userRoles?.[0]?.roleId ?? null) !== roleFilter) return false;
    return true;
  }), [crud.data, companyFilter, roleFilter]);

  const filtersActive = companyFilter !== '' || roleFilter !== '';
  const clearFilters = () => { setCompanyFilter(''); setRoleFilter(''); };

  const totalPages = Math.max(1, Math.ceil(crud.totalCount / crud.pageSize));
  const currentPage = Math.floor(crud.offset / crud.pageSize) + 1;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
      {/* Search + filters + grid */}
      <div
        style={{
          background: 'var(--color-panel)',
          border: '1px solid var(--color-border)',
          borderRadius: 10,
          width: 'fit-content',
          minWidth: '100%',
        }}
      >
        {/* Toolbar */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '14px 18px',
            borderBottom: '1px solid var(--color-border)',
            flexWrap: 'wrap',
          }}
        >
          <div
            style={{
              display: 'flex', alignItems: 'center', gap: 8, padding: '7px 12px',
              background: 'var(--color-panel-alt)', border: '1px solid var(--color-border)',
              borderRadius: 8, width: 280,
            }}
            className="focus-within:!border-[var(--color-accent-bright)]"
          >
            <Search size={15} style={{ color: 'var(--color-text-dim)' }} />
            <input
              placeholder="Search users…"
              value={crud.searchText}
              onChange={(e) => { crud.setSearchText(e.target.value); crud.setOffset(0); }}
              style={{ border: 'none', outline: 'none', background: 'transparent', fontSize: 13.5, flex: 1, fontFamily: 'var(--font-body)', color: 'var(--color-text)', width: '100%' }}
            />
            {crud.searchText && (
              <button
                onClick={() => { crud.setSearchText(''); crud.setOffset(0); }}
                aria-label="Clear search" title="Clear search"
                style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: 9, padding: 0, background: 'transparent', border: 'none', color: 'var(--color-text-dim)', cursor: 'pointer', flexShrink: 0 }}
              >
                <X size={13} />
              </button>
            )}
          </div>

          {/* Filter popover — Role (+ Company for SuperAdmin) */}
          <UsersFilterPopover
            isSuperAdmin={isSuperAdmin}
            companyFilter={companyFilter}
            roleFilter={roleFilter}
            companies={companies}
            roles={roles}
            onChangeCompany={setCompanyFilter}
            onChangeRole={setRoleFilter}
            onClear={clearFilters}
            activeCount={(companyFilter !== '' ? 1 : 0) + (roleFilter !== '' ? 1 : 0)}
          />

          {filtersActive && (
            <button
              onClick={clearFilters}
              style={{ fontSize: 12, color: 'var(--color-accent)', background: 'transparent', border: 'none', cursor: 'pointer', padding: '6px 8px', borderRadius: 5, fontFamily: 'var(--font-body)', fontWeight: 500 }}
            >
              Clear all
            </button>
          )}

          <div style={{ flex: 1 }} />

          <ViewModeSwitch
            value={viewMode}
            onChange={setViewMode}
            options={[
              { id: 'comfortable', tip: 'Comfortable view', Icon: Table2 },
              { id: 'compact', tip: 'Compact view', Icon: Rows3 },
            ]}
          />

          <div style={{ fontFamily: 'var(--font-mono)', fontSize: 12, color: 'var(--color-text-dim)' }}>
            {crud.loading
              ? 'Loading…'
              : filtersActive
                ? `${filteredData.length} of ${crud.data.length} on page · ${crud.totalCount} total`
                : `${crud.totalCount} record${crud.totalCount === 1 ? '' : 's'}`}
          </div>
        </div>

        {/* Header + rows (scrollable so columns never clip) */}
        <div >
          <div
            className={compact ? 'grid-bordered-compact' : 'grid-bordered'}
            style={{
              display: 'grid',
              gridTemplateColumns: compact ? COMPACT_TEMPLATE : FULL_TEMPLATE,
              columnGap: 0,
              padding: 0,
              alignItems: 'stretch',
              borderBottom: '1px solid var(--color-border)',
              background: 'var(--color-panel-alt)',
              position: 'sticky',
              top: 72,
              zIndex: 10,
              fontSize: 14,
              letterSpacing: 0.3,
              color: 'var(--color-text)',
              fontWeight: 600,
              fontFamily: 'var(--font-body)',
            }}
          >
            <SortHeader label="Username" col="Username" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
            <SortHeader label="Email" col="Email" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
            <SortHeader label="Company" col="CompanyId" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
            {compact ? (
              <SortHeader label="Name" col="FirstName" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
            ) : (
              <>
                <SortHeader label="First Name" col="FirstName" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
                <SortHeader label="Last Name" col="LastName" activeCol={crud.sortCol} dir={crud.sortDir} onClick={handleSort} />
                <div style={{ textAlign: 'right' }}>Actions</div>
              </>
            )}
          </div>

          {crud.loading ? (
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)', fontSize: 13 }}>Loading…</div>
          ) : filteredData.length === 0 ? (
            <div style={{ padding: 60, textAlign: 'center', color: 'var(--color-text-dim)' }}>
              <UsersIcon size={32} style={{ margin: '0 auto 12px', opacity: 0.4 }} />
              <div style={{ fontSize: 14, marginBottom: 4 }}>{filtersActive ? 'No users match these filters' : 'No users found'}</div>
              <div style={{ fontSize: 12 }}>{filtersActive ? 'Try clearing the filters.' : 'Add a new user to get started.'}</div>
            </div>
          ) : (
            <div>
              {filteredData.map((u, i) => (
                <UserRow
                  key={u.id ?? i}
                  user={u}
                  compact={compact}
                  isLast={i === filteredData.length - 1}
                  onEdit={() => openEdit(u)}
                  onDelete={() => u.id && handleDelete(u.id, u.username)}
                />
              ))}
            </div>
          )}
        </div>

        {/* Pagination footer */}
        <div
          style={{
            padding: '12px 18px',
            borderTop: '1px solid var(--color-border)',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            fontSize: 12,
            color: 'var(--color-text-dim)',
            background: 'var(--color-panel-alt)',
            borderRadius: '0 0 10px 10px',
          }}
        >
          <div>
            Showing <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{filteredData.length}</span>{' '}
            of <span style={{ color: 'var(--color-text-mid)', fontWeight: 500 }}>{crud.totalCount}</span> users
          </div>
          <PaginationControls currentPage={currentPage} totalPages={totalPages} onPage={(p) => crud.setOffset((p - 1) * crud.pageSize)} />
        </div>
      </div>

      {/* Form modal — flat (no section headings) */}
      <FormModal
        open={crud.modalOpen}
        onClose={crud.closeModal}
        title={crud.editingItem ? 'Edit User' : 'New User'}
        icon={<UsersIcon size={20} />}
        footer={
          <>
            <GhostButton onClick={crud.closeModal} disabled={saving}>Cancel</GhostButton>
            <PrimaryButton onClick={handleSave} disabled={saving || !!passwordMismatch}>
              {saving ? 'Saving…' : crud.editingItem ? 'Save changes' : 'Add User'}
            </PrimaryButton>
          </>
        }
      >
        <div className="grid gap-4 sm:grid-cols-2">
          <Field label="First Name" required error={submitted ? errors.firstName : undefined}>
            <TextInput value={form.firstName ?? ''} placeholder="e.g. Dave" autoComplete="off" error={submitted && !!errors.firstName} onChange={(e) => set('firstName', e.target.value)} />
          </Field>
          <Field label="Last Name" required error={submitted ? errors.lastName : undefined}>
            <TextInput value={form.lastName ?? ''} placeholder="e.g. Cooper" autoComplete="off" error={submitted && !!errors.lastName} onChange={(e) => set('lastName', e.target.value)} />
          </Field>

          <div className="sm:col-span-2">
            <Field label="Email" required error={submitted ? errors.email : undefined}>
              <TextInput type="email" value={form.email ?? ''} placeholder="e.g. name@example.com" autoComplete="off" error={submitted && !!errors.email} onChange={(e) => handleEmailChange(e.target.value)} />
            </Field>
          </div>
          <div className="sm:col-span-2">
            <Field label="Username" required error={submitted ? errors.username : undefined} hint="Username mirrors the email address.">
              <TextInput value={form.username ?? ''} readOnly autoComplete="off" style={{ background: 'var(--color-panel-alt)', color: 'var(--color-text-mid)' }} />
            </Field>
          </div>

          <Field label="User Role" required error={submitted ? errors.roleId : undefined}>
            <SelectInput value={roleId} error={submitted && !!errors.roleId} onChange={(e) => setRoleId(Number(e.target.value))}>
              {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
              {roles.length === 0 && <><option value={1}>SuperAdmin</option><option value={2}>CompanyAdmin</option><option value={3}>CompanyUser</option></>}
            </SelectInput>
          </Field>
          {isSuperAdmin ? (
            <Field label="Company" required error={submitted ? errors.companyId : undefined}>
              <SelectInput value={form.companyId ?? ''} error={submitted && !!errors.companyId} onChange={(e) => set('companyId', e.target.value !== '' ? Number(e.target.value) : null)}>
                <option value="">-- Select Company --</option>
                {roles.find(r => r.id === roleId)?.name === 'SuperAdmin' && <option value={0}>ALL</option>}
                {companies.map((c) => <option key={c.id} value={c.id}>{c.companyName}</option>)}
              </SelectInput>
            </Field>
          ) : (
            <Field label="Company">
              <TextInput value={authUser?.companyId ? `Company #${authUser.companyId}` : '-'} readOnly style={{ background: 'var(--color-panel-alt)', color: 'var(--color-text-mid)' }} />
            </Field>
          )}

          <Field label="Password" required={!crud.editingItem} error={submitted ? errors.password : undefined}>
            <PasswordInput
              value={form.password ?? ''}
              show={showPw}
              onToggle={() => setShowPw((s) => !s)}
              placeholder={crud.editingItem ? 'Leave blank to keep current' : 'Choose a strong password'}
              error={submitted && !!errors.password}
              onChange={(v) => set('password', v)}
            />
            <PasswordStrength value={form.password ?? ''} />
          </Field>
          <Field label="Confirm Password" error={passwordMismatch ? 'Passwords must match' : (submitted ? errors.confirmPassword : undefined)}>
            <PasswordInput
              value={confirmPassword}
              show={showConfirm}
              onToggle={() => setShowConfirm((s) => !s)}
              placeholder={crud.editingItem ? 'Leave blank to keep current' : 'Re-enter the password'}
              error={passwordMismatch || (submitted && !!errors.confirmPassword)}
              onChange={(v) => setConfirmPassword(v)}
            />
          </Field>
        </div>
      </FormModal>

      {crud.toast && <Toast message={crud.toast.message} type={crud.toast.type} onClose={() => crud.setToast(null)} />}
    </div>
  );
}

// ── Sub-components ───────────────────────────────────────────

// Password input with an integrated show/hide eye toggle.
function PasswordInput({
  value,
  onChange,
  show,
  onToggle,
  placeholder,
  error,
}: {
  value: string;
  onChange: (v: string) => void;
  show: boolean;
  onToggle: () => void;
  placeholder?: string;
  error?: boolean;
}) {
  return (
    <div style={{ position: 'relative' }}>
      <TextInput
        type={show ? 'text' : 'password'}
        value={value}
        placeholder={placeholder}
        autoComplete="new-password"
        error={error}
        style={{ paddingRight: 40 }}
        onChange={(e) => onChange(e.target.value)}
      />
      <button
        type="button"
        onClick={onToggle}
        aria-label={show ? 'Hide password' : 'Show password'}
        style={{
          position: 'absolute', right: 6, top: 5, width: 28, height: 28, borderRadius: 6,
          background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-dim)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
        }}
        onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--color-panel-alt)')}
        onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
      >
        {show ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
    </div>
  );
}

function UserRow({
  user: u,
  compact,
  isLast,
  onEdit,
  onDelete,
}: {
  user: User;
  compact: boolean;
  isLast: boolean;
  onEdit: () => void;
  onDelete: () => void;
}) {
  const [hover, setHover] = useState(false);
  const rowBg = hover ? 'var(--color-panel-alt)' : (compact ? 'var(--color-panel)' : 'transparent');
  const fullName = `${u.firstName ?? ''} ${u.lastName ?? ''}`.trim();

  return (
    <div
      onMouseEnter={() => setHover(true)}
      onMouseLeave={() => setHover(false)}
      className={compact ? 'grid-bordered-compact' : 'grid-bordered'}
      style={{
        display: 'grid',
        gridTemplateColumns: compact ? COMPACT_TEMPLATE : FULL_TEMPLATE,
        columnGap: 0,
        padding: 0,
        borderBottom: isLast ? 'none' : '1px solid var(--color-border)',
        alignItems: 'stretch',
        fontSize: 14,
        position: compact ? 'relative' : undefined,
        background: rowBg,
        transition: 'background 120ms',
      }}
    >
      <Cell ellipsis style={{ fontWeight: 500 }}>{u.username || '—'}</Cell>
      <Cell ellipsis>{u.email || '—'}</Cell>
      <Cell ellipsis>{u.company?.companyName ?? '—'}</Cell>
      {compact ? (
        <Cell ellipsis>{fullName || '—'}</Cell>
      ) : (
        <>
          <Cell ellipsis>{u.firstName || '—'}</Cell>
          <Cell ellipsis>{u.lastName || '—'}</Cell>
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 6 }}>
            <IconButton title="Edit" onClick={onEdit}><Pencil size={17} /></IconButton>
            <IconButton title="Delete" onClick={onDelete} hoverDanger><Trash2 size={17} /></IconButton>
          </div>
        </>
      )}
      {compact && (
        <GradientOverlayActions visible={hover} bgColor={rowBg} onEdit={onEdit} onDelete={onDelete} />
      )}
    </div>
  );
}

// Filter popover — Role (+ Company for SuperAdmin). Portal-positioned.
function UsersFilterPopover({
  isSuperAdmin,
  companyFilter,
  roleFilter,
  companies,
  roles,
  onChangeCompany,
  onChangeRole,
  onClear,
  activeCount,
}: {
  isSuperAdmin: boolean;
  companyFilter: number | '';
  roleFilter: number | '';
  companies: Company[];
  roles: { id: number; name: string }[];
  onChangeCompany: (v: number | '') => void;
  onChangeRole: (v: number | '') => void;
  onClear: () => void;
  activeCount: number;
}) {
  const [open, setOpen] = useState(false);
  const [coords, setCoords] = useState<{ top: number; left: number } | null>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const POP_WIDTH = 320;

  const updateCoords = useCallback(() => {
    const rect = btnRef.current?.getBoundingClientRect();
    if (!rect) return;
    const top = rect.bottom + 8;
    const left = Math.max(12, Math.min(rect.left, window.innerWidth - POP_WIDTH - 12));
    setCoords({ top, left });
  }, []);

  useLayoutEffect(() => { if (open) updateCoords(); }, [open, updateCoords]);
  useEffect(() => {
    if (!open) return;
    const onScroll = () => updateCoords();
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (popRef.current?.contains(t) || btnRef.current?.contains(t)) return;
      setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', updateCoords);
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', updateCoords);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, updateCoords]);

  const isActive = activeCount > 0;

  return (
    <>
      <button
        ref={btnRef}
        onClick={() => setOpen((o) => !o)}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 7, padding: '7px 12px',
          fontSize: 13, fontWeight: 500, fontFamily: 'var(--font-body)', cursor: 'pointer',
          background: open ? 'var(--color-panel-alt)' : 'var(--color-panel)',
          color: isActive ? 'var(--color-accent)' : 'var(--color-text-mid)',
          border: `1px solid ${isActive ? 'var(--color-accent)' : 'var(--color-border)'}`,
          borderRadius: 7, transition: 'all 120ms',
        }}
      >
        <SlidersHorizontal size={14} />
        Filter
        {isActive && (
          <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', minWidth: 18, height: 18, padding: '0 5px', borderRadius: 9, background: 'var(--color-accent)', color: '#fff', fontSize: 10.5, fontWeight: 700, fontFamily: 'var(--font-mono)', lineHeight: 1 }}>
            {activeCount}
          </span>
        )}
      </button>

      {open && coords && createPortal(
        <div
          ref={popRef}
          style={{
            position: 'fixed', top: coords.top, left: coords.left, width: POP_WIDTH,
            background: 'var(--color-panel)', border: '1px solid var(--color-border)',
            borderRadius: 10, boxShadow: '0 12px 32px rgba(15,23,32,0.12)', zIndex: 100, overflow: 'hidden',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '12px 16px', borderBottom: '1px solid var(--color-border)' }}>
            <div style={{ fontSize: 10.5, textTransform: 'uppercase', letterSpacing: 1.2, color: 'var(--color-text-dim)', fontFamily: 'var(--font-mono)', fontWeight: 500 }}>Filter by</div>
            <button onClick={() => setOpen(false)} aria-label="Close" style={{ width: 22, height: 22, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'transparent', border: 'none', cursor: 'pointer', color: 'var(--color-text-dim)', borderRadius: 4 }}>
              <X size={14} />
            </button>
          </div>

          <div style={{ padding: 16, display: 'flex', flexDirection: 'column', gap: 14 }}>
            <PopoverField label="Role">
              <FilterSelect
                value={roleFilter === '' ? '' : String(roleFilter)}
                onChange={(v) => onChangeRole(v === '' ? '' : Number(v))}
                placeholder="All roles"
                options={roles.map((r) => ({ value: String(r.id), label: r.name }))}
              />
            </PopoverField>
            {isSuperAdmin && (
              <PopoverField label="Company">
                <FilterSelect
                  value={companyFilter === '' ? '' : String(companyFilter)}
                  onChange={(v) => onChangeCompany(v === '' ? '' : Number(v))}
                  placeholder="All companies"
                  options={companies.map((c) => ({ value: String(c.id), label: c.companyName }))}
                />
              </PopoverField>
            )}
          </div>

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderTop: '1px solid var(--color-border)', background: 'var(--color-panel-alt)' }}>
            <button onClick={onClear} disabled={activeCount === 0} style={{ fontSize: 12, color: activeCount === 0 ? 'var(--color-text-dim)' : 'var(--color-accent)', background: 'transparent', border: 'none', cursor: activeCount === 0 ? 'not-allowed' : 'pointer', fontFamily: 'var(--font-body)', fontWeight: 500, padding: '4px 4px' }}>Clear filters</button>
            <button onClick={() => setOpen(false)} style={{ fontSize: 12.5, fontWeight: 500, background: 'var(--color-accent)', color: '#fff', border: 'none', padding: '6px 14px', borderRadius: 6, cursor: 'pointer', fontFamily: 'var(--font-body)' }}>Apply</button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}

function PopoverField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{ fontSize: 11.5, color: 'var(--color-text-mid)', fontFamily: 'var(--font-body)', fontWeight: 500 }}>{label}</span>
      {children}
    </label>
  );
}

function FilterSelect({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
}) {
  const hasValue = !!value;
  return (
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      style={{
        width: '100%', height: 34, padding: '0 30px 0 11px', fontSize: 13, fontFamily: 'var(--font-body)',
        color: hasValue ? 'var(--color-text)' : 'var(--color-text-mid)',
        background: `var(--color-panel) url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='10' height='6' viewBox='0 0 10 6' fill='none'><path d='M1 1l4 4 4-4' stroke='%238894a5' stroke-width='1.5' stroke-linecap='round' stroke-linejoin='round'/></svg>") no-repeat right 11px center`,
        border: `1px solid ${hasValue ? 'var(--color-accent)' : 'var(--color-border)'}`,
        borderRadius: 7, appearance: 'none', WebkitAppearance: 'none', MozAppearance: 'none',
        outline: 'none', cursor: 'pointer', fontWeight: hasValue ? 500 : 400, transition: 'border-color 120ms',
      }}
    >
      <option value="">{placeholder}</option>
      {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
    </select>
  );
}
